import { useMemo } from "react";
import { useQueries, UseQueryResult } from "react-query";
import { kebabCase } from "lodash";
import useQueryState from "./useQueryState";
import { getAnomalyTimeseriesByGeographicalUnit } from "../services/timeseries";
import Cookies from "js-cookie";
import { useAuthContext } from "./useAuthContext";
import { useUIStore } from "../store/ui";
import { buildIndicatorName } from "../helpers/indicatorName";

type QueryResult = UseQueryResult<any, unknown>;

const accessToken = () => Cookies.get("accessToken");

const useTimeseriesAnomaly = ({ isEnabled }: { isEnabled?: boolean }) => {
  const { parsedToken } = useAuthContext();
  const { email: userEmail } = parsedToken || {};
  const {
    queryState: { country, geographicalUnit, aggregationLevel, layers },
    queryState,
  } = useQueryState();
  const isDetrended = useUIStore((s) => s.isDetrended);
  const isLandCoverMasked = useUIStore((s) => s.isLandCoverMasked);
  const isSystemType =
    aggregationLevel === "0" ||
    aggregationLevel === "1" ||
    aggregationLevel === "2";

  const queries = useQueries(
    layers.map(({ displayName, months, dekads, season, dataunit }) => {
      const indicatorName = buildIndicatorName(displayName, {
        isLandCoverMasked,
        isDetrended,
      });

      return {
        queryKey: [
          "timeseries",
          "anomaly",
          country,
          indicatorName,
          geographicalUnit,
          aggregationLevel,
          months,
          dekads,
          season,
          isSystemType ? "system" : "user",
          accessToken() ? userEmail : "",
          isDetrended,
          isLandCoverMasked,
        ],
        queryFn: async () => {
          const res: any = await getAnomalyTimeseriesByGeographicalUnit({
            country,
            indicator: indicatorName,
            locationId: geographicalUnit,
            adminLevel: aggregationLevel,
            months,
            dekads,
            type: isSystemType ? "system" : "user",
          });

          return {
            ...res,
            data: { ...res.data, season, dataunit },
          };
        },
        enabled: Boolean(
          isEnabled &&
          country &&
          displayName &&
          displayName !== "Hybrid Index" &&
          geographicalUnit &&
          aggregationLevel &&
          months.every((m) => !!m) &&
          dekads.every((d) => !!d) &&
          displayName !== "Hybrid Index" &&
          season &&
          queryState.season.includes(season),
        ),
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        retry: false,
      };
    }),
  ) as QueryResult[];

  const data = useMemo(() => {
    return queries
      .filter((q) => q.isSuccess && q.data)
      .map((q) => q.data?.data)
      .flat();
  }, [queries]);

  const isFetchingAny = queries.some((q) => q.isFetching);
  const isErrorAny = queries.some((q) => q.isError);

  return {
    data,
    isFetching: isFetchingAny,
    isError: isErrorAny,
  };
};

export default useTimeseriesAnomaly;
